import React, { useState, useEffect, useCallback } from 'react';
import { GovernanceColors } from '@/config/governanceColors';
import { Activity, AlertTriangle, Shield, CheckCircle2, RefreshCw, Play } from 'lucide-react';
import {
  fetchTrustAnomalyStats,
  fetchTrustAnomalyNetwork,
  fetchTrustAnomalyHealth,
  triggerTrustAnomalyScan,
} from '../../lib/api';

// CODE-002 closure (2026-06-19): trust-anomaly-detector mounted at
// /api/trust-anomaly-detector/* with unifiedAuth gate. This view surfaces
// the capsule's already-running background scan loop (5-min cadence,
// scheduled inside CapsuleTrustAnomalyDetector core) plus statistics +
// network-trust metrics + manual scan trigger.
//
// Honest scope: this is the OPERATOR-side surface. The capability already
// runs autonomously; the view makes it visible + manually triggerable so
// operators see what their build is doing, not invent new logic.
export function TrustAnomalyView() {
  const [stats, setStats] = useState<any>(null);
  const [network, setNetwork] = useState<any>(null);
  const [health, setHealth] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [lastScan, setLastScan] = useState<any>(null);
  const [error, setError] = useState<string>('');

  const refresh = useCallback(async () => {
    setError('');
    const [s, n, h] = await Promise.all([
      fetchTrustAnomalyStats(),
      fetchTrustAnomalyNetwork(),
      fetchTrustAnomalyHealth(),
    ]);
    setStats(s);
    setNetwork(n);
    setHealth(h);
    setLoading(false);
    if (!s && !n && !h) {
      setError('No data from /api/trust-anomaly-detector/* -- check auth or container');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const onScan = async () => {
    setScanning(true);
    setError('');
    const result = await triggerTrustAnomalyScan();
    if (result) {
      setLastScan(result);
      // Refresh stats so the dashboard reflects the new scan.
      await refresh();
    } else {
      setError('Manual scan failed -- /api/trust-anomaly-detector/scan returned no payload');
    }
    setScanning(false);
  };

  const healthOk = health?.status === 'ok' || health?.status === 'healthy';

  return (
    <div className="p-6">
      <header className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Activity className="w-6 h-6" style={{ color: GovernanceColors.primary }} aria-hidden="true" />
            Trust Anomaly Detector
          </h1>
          <p className="text-sm text-[#A8A29E] mt-1">
            Continuous trust-network scan running inside the capsule. Detects signature mismatches, behavior drift, and trust-chain anomalies across the agent fleet. CODE-002 mounted 2026-06-19.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={refresh}
            className="px-3 py-2 text-sm rounded border border-[#3A352F] text-[#F5F0E8] hover:bg-[#292524] flex items-center gap-2"
            aria-label="Refresh trust-anomaly statistics"
          >
            <RefreshCw className="w-4 h-4" aria-hidden="true" />
            Refresh
          </button>
          <button
            onClick={onScan}
            disabled={scanning}
            // Hover was an agentbilly-era purple that maps to no current token. A copper
            // button hovers to the copper hover value; there is no purple in this brand.
            className="px-3 py-2 text-sm rounded bg-[var(--gc-primary)] text-white hover:bg-[var(--gc-primary-light)] disabled:opacity-50 flex items-center gap-2"
            style={{
              '--gc-primary': GovernanceColors.primary,
              '--gc-primary-light': GovernanceColors.primaryLight,
            } as React.CSSProperties}
            aria-label="Trigger manual trust-anomaly scan"
          >
            <Play className="w-4 h-4" aria-hidden="true" />
            {scanning ? 'Scanning…' : 'Run scan'}
          </button>
        </div>
      </header>

      {error && (
        <div className="mb-4 p-3 rounded border border-amber-800 bg-amber-950/30 text-sm text-amber-200 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5" aria-hidden="true" />
          {error}
        </div>
      )}

      <section aria-label="Detector health" className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="p-4 rounded border border-[#2E2A26] bg-[#1C1917]">
          <div className="text-xs uppercase tracking-wide text-[#A8A29E] mb-2">Detector health</div>
          <div className="flex items-center gap-2">
            {healthOk ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-400" aria-hidden="true" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-amber-400" aria-hidden="true" />
            )}
            <span className="text-base font-medium">{health?.status ?? (loading ? '…' : 'unknown')}</span>
          </div>
          {health?.uptime !== undefined && (
            <div className="text-xs text-[#A8A29E] mt-2">uptime: {String(health.uptime)}</div>
          )}
        </div>

        <div className="p-4 rounded border border-[#2E2A26] bg-[#1C1917]">
          <div className="text-xs uppercase tracking-wide text-[#A8A29E] mb-2">Agents scanned (last cycle)</div>
          <div className="text-2xl font-semibold">{stats?.agentsScanned ?? stats?.last_scan?.agentsScanned ?? '--'}</div>
          {stats?.totalScans !== undefined && (
            <div className="text-xs text-[#A8A29E] mt-1">cumulative scans: {stats.totalScans}</div>
          )}
        </div>

        <div className="p-4 rounded border border-[#2E2A26] bg-[#1C1917]">
          <div className="text-xs uppercase tracking-wide text-[#A8A29E] mb-2">Anomalies found (last cycle)</div>
          <div className={`text-2xl font-semibold ${(stats?.anomaliesFound ?? 0) > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
            {stats?.anomaliesFound ?? stats?.last_scan?.anomaliesFound ?? '--'}
          </div>
          {stats?.systemTrustHealth && (
            <div className="text-xs text-[#A8A29E] mt-1">system trust: {String(stats.systemTrustHealth)}</div>
          )}
        </div>
      </section>

      <section aria-label="Network trust metrics" className="mb-6">
        <h2 className="font-body text-sm font-semibold text-[#A8A29E] uppercase tracking-wide mb-3 flex items-center gap-2">
          <Shield className="w-4 h-4" aria-hidden="true" />
          Network trust metrics
        </h2>
        <div className="p-4 rounded border border-[#2E2A26] bg-[#1C1917]">
          {network ? (
            <pre className="text-xs text-[#F5F0E8] overflow-x-auto whitespace-pre-wrap">
              {JSON.stringify(network, null, 2)}
            </pre>
          ) : (
            <div className="text-sm text-[#A8A29E]">
              {loading ? 'Loading…' : 'No network-metrics payload returned'}
            </div>
          )}
        </div>
      </section>

      {lastScan && (
        <section aria-label="Last manual scan result">
          <h2 className="text-sm font-semibold text-[#A8A29E] uppercase tracking-wide mb-3">Last manual scan result</h2>
          <div className="p-4 rounded border border-[#2E2A26] bg-[#1C1917]">
            <pre className="text-xs text-[#F5F0E8] overflow-x-auto whitespace-pre-wrap">
              {JSON.stringify(lastScan, null, 2)}
            </pre>
          </div>
        </section>
      )}
    </div>
  );
}

export default TrustAnomalyView;
